import * as React from 'react'

import { Box, Grid } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import i18n from 'modules/core/lib/i18n'

import RowContainer from 'modules/contracts/components/ContractDetails/Section'
import BlankSection from 'modules/contracts/components/ContractDetails/Section/Blank'
import { contractVersionEmployee } from 'modules/contracts/lib/contractVersionEmployee'
import type ContractVersion from 'modules/contracts/store/models/ContractVersion'
import employees from 'modules/employees/store/collections/employees'
import jobLevelsCollection from 'modules/job_catalog/store/collections/levels'
import { jobLevelRole } from 'modules/job_catalog/store/collections/roles'
import policyAuthorizations from 'modules/permissions/store/collections/policyAuthorizations'

import CurrentPosition from './CurrentPosition'
import JobDescription from './JobDescription'
import PreferredJobPosition from './PreferredJobPosition'
import RoleDescription from './RoleDescription'

interface Props {
  contractVersion: ContractVersion
}

const JobPosition = ({ contractVersion }: Props) => {
  const canSee = policyAuthorizations.for(
    'contracts.see_job_title',
    contractVersionEmployee(contractVersion, employees)
  )
  if (!canSee) return null

  const canEdit = policyAuthorizations.for(
    'contracts.edit',
    contractVersionEmployee(contractVersion, employees)
  )
  const jobCatalogLevel = jobLevelsCollection.get(contractVersion.attributes.job_catalog_level_id)
  const jobCatalogRole = jobCatalogLevel ? jobLevelRole(jobCatalogLevel) : null
  const hasCurrentPosition = !!jobCatalogLevel && !!jobCatalogRole
  const hasRoleDescription = !!jobCatalogRole?.attributes.description
  const hasJobDescription = contractVersion.attributes.es_job_description !== null
  const hasPreferredJobPosition = contractVersion.attributes.job_title !== null
  const showCurrentPosition = canEdit || (jobCatalogLevel && jobCatalogRole)
  const showRoleDescription = canEdit || jobCatalogRole?.attributes.description
  const showJobDescription =
    (canEdit || contractVersion.attributes.es_job_description) && contractVersion.isSpanish
  const showPreferredJobPosition = canEdit || contractVersion.attributes.job_title
  const hasContent =
    hasCurrentPosition || hasRoleDescription || hasJobDescription || hasPreferredJobPosition
  const showEmptySlate = canEdit && !hasContent

  if (!hasContent && !canEdit) return null

  const showLeftColumn = !showEmptySlate && (showCurrentPosition || showRoleDescription)

  return (
    <RowContainer icon="briefcase" title={i18n.t('contract_versions.job_position.title')}>
      <Grid variant="fixed">
        {showLeftColumn && (
          <Box gridColSpan={{ xxs: 12, xs: 12, s: 6 }} gap="s32" paddingRight="s16">
            {showCurrentPosition && jobCatalogRole && (
              <CurrentPosition jobCatalogLevel={jobCatalogLevel} jobCatalogRole={jobCatalogRole} />
            )}

            {showRoleDescription && (
              <RoleDescription jobCatalogDescription={jobCatalogRole?.attributes.description} />
            )}
          </Box>
        )}
        {!showEmptySlate && (
          <Box gridColSpan={{ xxs: 12, xs: 12, s: 6 }} gap="s32" paddingLeft="s16">
            {showPreferredJobPosition && (
              <PreferredJobPosition preferredJobPosition={contractVersion.attributes.job_title} />
            )}
            {showJobDescription && (
              <JobDescription
                jobDescription={contractVersion.attributes.es_job_description ?? undefined}
              />
            )}
          </Box>
        )}
        {showEmptySlate && (
          <BlankSection
            employee={contractVersionEmployee(contractVersion, employees)!}
            contractVersion={contractVersion}
            icon="inbox"
            title={i18n.t('contract_versions.job_position.blank_slate.title')}
            description={i18n.t('contract_versions.job_position.blank_slate.description')}
            buttonText={i18n.t('contract_versions.job_position.blank_slate.button')}
          />
        )}
      </Grid>
    </RowContainer>
  )
}
export default observer(JobPosition)
